<script setup lang="ts">
import { ref, watch } from 'vue';
import { useDebounce } from '@vueuse/core';
import { useDataTableFilters } from '@DataTableModule';

const props = withDefaults(
  defineProps<{
    customClass?: string;
    debounceTime?: number;
    loading?: boolean;
    placeholder?: string;
  }>(),
  {
    debounceTime: 350,
    placeholder: 'common.search',
  }
);

const { page } = useDataTableFilters();

const search = defineModel<string>();

const _search = ref(search.value || '');
const _debounceSearch = useDebounce(_search, props.debounceTime);

watch(_debounceSearch, (newValue) => {
  const value = newValue ?? '';
  const trimmed = value.trim();
  const startsWithNumber = /^\d/.test(trimmed);

  // Non-numeric searches: only emit when 2+ characters; otherwise clear.
  // Numeric searches: emit immediately (even 1 character).
  if (startsWithNumber) return (search.value = value);

  search.value = trimmed.length >= 2 ? value : '';
});
watch(search, () => (_search.value = search.value as string));
watch(_search, () => (page.value = 1));
</script>

<template>
  <IconField
    class="relative"
    iconPosition="left"
    :loading
    :class="customClass"
  >
    <InputIcon class="pi pi-search" />

    <InputText
      v-model="_search"
      :placeholder="$t(placeholder)"
      :pt:root="[
        _search ? 'border-warning-500' : '',
        'h-9 p-1 pl-8 text-sm w-full sm:max-w-56 2xl:max-w-none',
        customClass,
      ]"
    />

    <Button
      v-show="_search"
      rounded
      aria-label="Cancel"
      icon="pi pi-times"
      pt:icon="text-[10px] font-semibold"
      pt:root="absolute right-2 top-1/2 size-5 -translate-y-1/2 border-gray-400 bg-gray-400 p-0"
      @click="_search = ''"
    />

    <i
      v-if="_search"
      class="pi pi-circle-fill absolute right-[-6px] top-[-4px] text-sm text-warning-500"
    />
  </IconField>
</template>
